// Phase HR-003 Wave 6 — Training + Floor event API client.
import apiClient from '@/shared/api/client'

// ── Training ───────────────────────────────────────────────────────

export type KpiDirection = 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER'
export type TrainingSessionStatus =
  | 'PLANNED' | 'DELIVERED' | 'CANCELLED' | 'RESCHEDULED'

export interface TrainingProgram {
  id: string
  program_code: string
  program_name: string
  description: string | null
  category: string | null
  target_kpi_code: string | null
  expected_kpi_direction: KpiDirection | null
  duration_minutes: number | null
  materials_url: string | null
  active: boolean
  created_at: string
}

export interface TrainingProgramCreate {
  program_code: string
  program_name: string
  description?: string | null
  category?: string | null
  target_kpi_code?: string | null
  expected_kpi_direction?: KpiDirection | null
  duration_minutes?: number | null
  materials_url?: string | null
}

export interface TrainingSession {
  id: string
  program_id: string
  program_name: string | null
  target_kpi_code: string | null
  session_no: string
  batch_code: string | null
  plant_id: string
  department_id: string | null
  department_name: string | null
  scheduled_date: string
  actual_date: string | null
  trainer_user_id: string | null
  trainer_name: string | null
  trainer_name_external: string | null
  location: string | null
  status: TrainingSessionStatus
  remarks: string | null
  attendee_count: number
  attended_count: number
  avg_pre_score: string | null
  avg_post_score: string | null
  created_at: string
}

export interface SessionCreate {
  program_id: string
  plant_id: string
  scheduled_date: string
  batch_code?: string | null
  department_id?: string | null
  trainer_user_id?: string | null
  trainer_name_external?: string | null
  location?: string | null
  remarks?: string | null
}

export interface AttendanceRow {
  id?: string
  employee_id: string
  emp_code?: string | null
  employee_name?: string | null
  attended: boolean
  pre_test_score?: number | null
  post_test_score?: number | null
  pass_threshold_pct?: number | null
  passed?: boolean | null
  supervisor_validation_notes?: string | null
  baseline_kpi_value?: number | null
  post_30d_kpi_value?: number | null
  post_60d_kpi_value?: number | null
  post_90d_kpi_value?: number | null
}

// ── Floor events ───────────────────────────────────────────────────

export type ActivityCode =
  | 'PRODUCTIVE' | 'WAITING_MATERIAL' | 'WAITING_INSTRUCTION'
  | 'WAITING_FITTER' | 'WALKING' | 'CLEANING'
  | 'IDLE' | 'BREAK' | 'TALKING' | 'OTHER'

export type StoppageType = 'IDLE' | 'MICRO_STOP'

export type StoppageReason =
  | 'NO_MATERIAL' | 'NO_OPERATOR' | 'NO_FITTER' | 'NO_QUALITY_OK'
  | 'CHANGEOVER' | 'BREAKDOWN' | 'POWER' | 'STARVATION'
  | 'CLEANING' | 'SHIFT_HANDOVER' | 'OTHER'

export interface ActivitySample {
  id: string
  sample_at: string
  plant_id: string
  department_id: string | null
  department_name: string | null
  section_code: string | null
  observed_employee_id: string | null
  observed_employee_name: string | null
  machine_id: string | null
  machine_code: string | null
  activity_code: ActivityCode
  remark: string | null
  observer_user_id: string | null
}

export interface StoppageEvent {
  id: string
  stoppage_type: StoppageType
  machine_id: string
  machine_code: string | null
  plant_id: string
  department_id: string | null
  department_name: string | null
  started_at: string
  ended_at: string | null
  duration_seconds: number | null
  duration_minutes: number | null
  reason_code: StoppageReason | null
  reason_text: string | null
  attributed_employee_id: string | null
  attributed_employee_name: string | null
  captured_by: string | null
  created_at: string
}

const BASE = '/hr'

export const trainingApi = {
  listPrograms: async (active_only = true): Promise<TrainingProgram[]> =>
    (await apiClient.get<TrainingProgram[]>(`${BASE}/training/programs`,
      { params: { active_only } })).data,
  createProgram: async (body: TrainingProgramCreate) =>
    (await apiClient.post<TrainingProgram>(`${BASE}/training/programs`, body)).data,
  listSessions: async (params?: {
    plant_id?: string; program_id?: string; status?: TrainingSessionStatus
    from_date?: string; to_date?: string; limit?: number
  }) => (await apiClient.get<TrainingSession[]>(`${BASE}/training/sessions`, { params })).data,
  createSession: async (body: SessionCreate) =>
    (await apiClient.post<TrainingSession>(`${BASE}/training/sessions`, body)).data,
  patchSession: async (id: string, patch: Partial<TrainingSession>) =>
    (await apiClient.patch<TrainingSession>(`${BASE}/training/sessions/${id}`, patch)).data,
  getAttendance: async (sessionId: string): Promise<AttendanceRow[]> =>
    (await apiClient.get<AttendanceRow[]>(
      `${BASE}/training/sessions/${sessionId}/attendance`)).data,
  upsertAttendance: async (sessionId: string, rows: AttendanceRow[]) =>
    (await apiClient.post<AttendanceRow[]>(
      `${BASE}/training/sessions/${sessionId}/attendance`,
      { session_id: sessionId, rows })).data,
}

export const floorApi = {
  createSample: async (body: {
    plant_id: string; activity_code: ActivityCode
    department_id?: string | null; section_code?: string | null
    observed_employee_id?: string | null; machine_id?: string | null
    remark?: string | null
  }) => (await apiClient.post<ActivitySample>(`${BASE}/floor/activity-samples`, body)).data,
  listSamples: async (params?: {
    plant_id?: string; department_id?: string
    from_dt?: string; to_dt?: string; limit?: number
  }) => (await apiClient.get<ActivitySample[]>(`${BASE}/floor/activity-samples`, { params })).data,
  distribution: async (params?: {
    plant_id?: string; department_id?: string
    from_dt?: string; to_dt?: string
  }) => (await apiClient.get<{ total: number; distribution: Record<string, number>; productive_pct: number }>(
    `${BASE}/floor/activity-distribution`, { params })).data,

  createStoppage: async (body: {
    plant_id: string; machine_id: string; stoppage_type: StoppageType
    started_at: string; department_id?: string | null
    ended_at?: string | null; reason_code?: StoppageReason | null
    reason_text?: string | null; attributed_employee_id?: string | null
  }) => (await apiClient.post<StoppageEvent>(`${BASE}/floor/stoppages`, body)).data,
  endStoppage: async (id: string, body: {
    ended_at: string; reason_code?: StoppageReason | null
    reason_text?: string | null; attributed_employee_id?: string | null
  }) => (await apiClient.post<StoppageEvent>(`${BASE}/floor/stoppages/${id}/end`, body)).data,
  listStoppages: async (params?: {
    plant_id?: string; department_id?: string; machine_id?: string
    stoppage_type?: StoppageType; reason_code?: StoppageReason
    open_only?: boolean; from_dt?: string; to_dt?: string; limit?: number
  }) => (await apiClient.get<StoppageEvent[]>(`${BASE}/floor/stoppages`, { params })).data,
}
